import { Fragment, useState } from 'react';
import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import { serializedTypes } from '@/reports/types';
import { Text, Stack, Box, Button, Table, Link } from '@/theme/index';
import { commaFormat, commaFormatCurrency } from '@/utils/index';
import { MarketplaceCarousel } from './MarketplaceCarousel/MarketplaceCarousel';
import { ValuationLineCard } from './ValuationLineCard/ValuationLineCard';
import { ValuationModal } from './ValuationModal/ValuationModal';
import { VehicleValuationFilter } from './VehicleValuationFilter/VehicleValuationFilter';
import { useMarketplaceSearch } from './hooks/useMarketplaceSearch';
import { useVehicleValuation } from './hooks/useVehicleValuation';
import { OutlinedPillCard } from '../../shared/OutlinedPillCard/OutlinedPillCard';

/**
 * Props for the VehicleValuation component.
 */
interface VehicleValuationProps {
  /**
   * The vehicle report containing data about the vehicle.
   */
  vehicleReport: serializedTypes.SerializedVehicleReport;
}

/**
 * A component that displays the market value and valuation details of a vehicle.
 * @param {VehicleValuationProps} props - The props for the component.
 * @returns {JSX.Element} - The JSX element representing the VehicleValuation component.
 */

export function VehicleValuation({ vehicleReport }: VehicleValuationProps) {
  const [modal, setModelState] = useState({
    modalOpen: false,
    modalType: '',
  });

  const toggleModal = (modalType: string | null) => {
    setModelState({
      modalOpen: !modal.modalOpen,
      modalType: modalType || '',
    });
  };

  const report = vehicleReport?.automobiles[0];
  const defaultData = {
    mileage: report?.mileage?.miles || 0,
    region: report?.valuation?.region?.name || 'ny',
    vin: report?.vin || '',
    plate: vehicleReport.meta?.search_data?.license_plate || '',
    state: vehicleReport.meta?.search_data?.state || '',
    defaultValuation: report?.valuation,
  };

  const {
    data: marketplaceData,
    isLoading: marketplaceIsLoading,
    isError: marketplaceIsError,
  } = useMarketplaceSearch(vehicleReport?.meta?.search_data);
  const { data: currentValuation } = useVehicleValuation(defaultData);

  const vehicleValuationValue =
    commaFormatCurrency(currentValuation?.clean_retail?.adjusted) || '-';
  const averageTradeValue =
    commaFormatCurrency(currentValuation?.average_trade?.adjusted) || '-';
  const cleanTradeValue =
    commaFormatCurrency(currentValuation?.clean_trade?.adjusted) || '-';
  const roughTradeValue =
    commaFormatCurrency(currentValuation?.rough_trade?.adjusted) || '-';
  const ymm = report ? `${report?.year} ${report?.make} ${report?.model}` : '-';
  const valuationMileage = commaFormat(currentValuation?.input_mileage);

  const valuationCount = () => {
    if (currentValuation === undefined) return 0;
    const keys = [
      'clean_trade',
      'average_trade',
      'rough_trade',
      'clean_retail',
    ];
    const reducer = (count: number, key: string) =>
      currentValuation && Boolean(currentValuation[key]) ? count + 1 : count;
    return keys.reduce(reducer, 0);
  };

  const displayValuationText = (type: string) => {
    if (valuationCount()) {
      return (
        <Text variant="h2" mb={0} textAlign="center" fontWeight="300">
          <strong>{type}</strong> based on{' '}
          <strong>{valuationMileage ? `${valuationMileage} mi.` : '-'}</strong>{' '}
          in <strong>{currentValuation?.region?.name}</strong>
        </Text>
      );
    }
  };

  const generateDetailsArray = () => {
    if (!marketplaceData || !marketplaceData.marketStats) return [];
    const { price, miles } = marketplaceData.marketStats;
    const priceMean = commaFormatCurrency(Math.round(price?.mean)) || '-';
    const milesMean = commaFormat(Math.round(miles?.mean)) || '-';
    const countMean = commaFormat(price.count) || '-';
    const minMean = commaFormatCurrency(price.min) || '-';
    const maxMean = commaFormatCurrency(price.max) || '-';
    return [
      ['Average price', priceMean],
      ['Average mileage', milesMean],
      ['Number of listings', countMean],
      ['Price range', `${minMean} - ${maxMean}`],
    ];
  };

  const handleClick = () => {
    toggleModal('averageMarketValue');
  };

  return (
    <ReportSection
      title="Possible Market Value"
      hasContent={valuationCount() > 0}
      sectionId="vehicle-market"
      customEmptyText={
        <EmptyVehicleSectionDisclaimer title="possible market value" />
      }
    >
      <Stack>
        <Text marginY={2}>
          Market values is an estimate of what the vehicle is valued at based on
          recent sales of similar vehicles.
        </Text>
        <VehicleValuationFilter defaultInputs={defaultData} />
      </Stack>
      <Box margin={2} textAlign="center">
        {displayValuationText('Retail value')}
        <Text mb={1}>
          This is the market value of this vehicle when sold through a private
          party or dealership
        </Text>
      </Box>
      <OutlinedPillCard
        outlined
        chipText="MOST COMMON VALUE WHEN SOLD"
        chipTextColor="#fff"
        outlineColor="primary"
      >
        <Button
          variant="text"
          sx={{
            textWeight: '300',
            textDecoration: 'underline',
          }}
          color="success"
          onClick={handleClick}
        >
          <Text variant="h3" fontWeight="300">
            Average market value
          </Text>
        </Button>
        <Text variant="h2">{vehicleValuationValue}</Text>
      </OutlinedPillCard>
      <Box margin={2}>
        {displayValuationText('Trade-in value')}
        <Text mt={1} textAlign="center">
          Dealers will generally pay less than the retail value since they need
          to resell it and make a profit
        </Text>
      </Box>
      <ValuationLineCard
        toggleModal={toggleModal}
        cleanTradeValue={cleanTradeValue}
        averageTradeValue={averageTradeValue}
        roughTradeValue={roughTradeValue}
      />
      {marketplaceData?.marketplaceListings?.length > 0 && (
        <>
          <Box>
            <Text variant="h2" textAlign="center">
              Comparable vehicle for sale
            </Text>
          </Box>
          <Box>
            <Text textAlign="center">
              See the price of similar {ymm} nearby currently for sale.
            </Text>
            <Box
              sx={{
                width: 'fit-content',
                display: 'flex',
                justifyContent: 'center',
                margin: '0 auto',
              }}
            >
              <Table rows={generateDetailsArray()} />
            </Box>
            <Box display="flex" justifyContent="center" marginY={2.6}>
              <Link href={marketplaceData?.marketplaceURL} color="success.main">
                Edit criteria
              </Link>
            </Box>
          </Box>
          <MarketplaceCarousel
            marketplaceListings={marketplaceData?.marketplaceListings}
            isLoading={marketplaceIsLoading}
            isError={marketplaceIsError}
          />
        </>
      )}
      {modal.modalOpen && (
        <ValuationModal modal={modal} toggleModal={toggleModal} />
      )}
    </ReportSection>
  );
}
